import { createContext, useContext, useId, type ReactNode } from 'react';

/**
 * The ARIA wiring a control needs, published to whatever renders inside.
 *
 * Context rather than a render prop, so call sites keep the plain shape
 * `<Field label="X"><Input /></Field>` — the wiring is invisible and cannot be
 * forgotten at a call site.
 */
export interface FieldControlProps {
  id?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
  required?: boolean;
}

const FieldContext = createContext<FieldControlProps>({});

/** Read by Input, Textarea and Select. Returns `{}` outside a Field. */
export function useFieldControl(): FieldControlProps {
  return useContext(FieldContext);
}

interface FieldProps {
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}

export function Field({ label, error, hint, required, children }: FieldProps) {
  const base = useId();
  const controlId = `${base}-control`;
  const labelId = `${base}-label`;
  const messageId = `${base}-message`;
  // Error supersedes hint: announcing both would read out advice the user has
  // already failed to follow.
  const message = error ?? hint;

  const control: FieldControlProps = {
    id: controlId,
    'aria-labelledby': labelId,
    ...(message ? { 'aria-describedby': messageId } : {}),
    ...(error ? { 'aria-invalid': true as const } : {}),
    ...(required ? { required: true } : {}),
  };

  return (
    <div className="block">
      <label htmlFor={controlId} id={labelId} className="mb-1 block text-sm font-medium text-ink-700">
        {label}
        {required && (
          <span className="ml-0.5 text-red-600" aria-hidden="true">
            *
          </span>
        )}
      </label>

      <FieldContext.Provider value={control}>{children}</FieldContext.Provider>

      {message && (
        <span
          id={messageId}
          className={error ? 'mt-1 block text-xs text-red-600' : 'mt-1 block text-xs text-ink-500'}
        >
          {message}
        </span>
      )}
    </div>
  );
}
